import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { 
  Truck, 
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Building2,
  Mail,
  Globe,
  ChevronRight,
  Eye,
  Ban,
  Trash2,
  RefreshCcw,
  Settings,
  Euro
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { toast } from 'sonner';
import { getUserErrorMessage } from '../../utils/errorMessages';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  approved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  suspended: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  blocked: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  inactive: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-400',
};

const statusLabels = {
  pending: 'In behandeling',
  approved: 'Goedgekeurd',
  suspended: 'Geschorst',
  blocked: 'Geblokkeerd',
  inactive: 'Inactief',
};

const AdminSupplierManagement = () => {
  const { api } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [leadFee, setLeadFee] = useState(50);
  
  // Detail dialog
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Action dialogs
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [actionNotes, setActionNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Settings dialog
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [newLeadFee, setNewLeadFee] = useState(50);
  
  // Profile reviews
  const [activeTab, setActiveTab] = useState('suppliers');
  const [pendingReviews, setPendingReviews] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');

  const fetchSuppliers = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      const response = await api.get('/admin/suppliers', { params });
      setSuppliers(response.data.suppliers);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
      toast.error('Kon leveranciers niet laden');
    }
  }, [api, statusFilter, searchQuery]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/admin/suppliers/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [api]);

  const fetchLeadFee = useCallback(async () => {
    try {
      const response = await api.get('/admin/suppliers/settings/lead-fee');
      setLeadFee(response.data.lead_fee);
      setNewLeadFee(response.data.lead_fee);
    } catch (error) {
      console.error('Failed to fetch lead fee:', error);
    }
  }, [api]);

  const fetchPendingReviews = useCallback(async () => {
    try {
      const response = await api.get('/admin/suppliers/reviews/pending');
      setPendingReviews(response.data.suppliers || []);
    } catch (error) {
      console.error('Failed to fetch pending reviews:', error);
    }
  }, [api]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSuppliers(), fetchStats(), fetchLeadFee(), fetchPendingReviews()]);
      setLoading(false);
    };
    loadData();
  }, [fetchSuppliers, fetchStats, fetchLeadFee, fetchPendingReviews]);

  const handleViewDetail = async (supplier) => {
    try {
      const response = await api.get(`/admin/suppliers/${supplier.id}`);
      setSelectedSupplier(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Kon details niet laden');
    }
  };

  const handleApprove = async (approve) => {
    if (!selectedSupplier) return;
    setProcessing(true);
    
    try {
      await api.post(`/admin/suppliers/${selectedSupplier.supplier.id}/approve`, {
        action: approve ? 'approve' : 'reject',
        notes: actionNotes
      });
      toast.success(approve ? 'Leverancier goedgekeurd' : 'Leverancier afgewezen');
      setApproveDialogOpen(false);
      setDetailDialogOpen(false);
      setActionNotes('');
      fetchSuppliers();
      fetchStats();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Actie mislukt'));
    } finally {
      setProcessing(false);
    }
  };

  const handleStatusChange = async () => {
    if (!selectedSupplier || !newStatus || !actionNotes) return;
    setProcessing(true);
    
    try {
      await api.post(`/admin/suppliers/${selectedSupplier.supplier.id}/status`, {
        status: newStatus,
        reason: actionNotes
      });
      toast.success('Status gewijzigd');
      setStatusDialogOpen(false);
      setDetailDialogOpen(false);
      setActionNotes('');
      setNewStatus('');
      fetchSuppliers();
      fetchStats();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Status wijzigen mislukt'));
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSupplier) return;
    setProcessing(true);
    
    try {
      await api.delete(`/admin/suppliers/${selectedSupplier.supplier.id}`);
      toast.success('Leverancier verwijderd');
      setDeleteDialogOpen(false);
      setDetailDialogOpen(false);
      fetchSuppliers();
      fetchStats();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Verwijderen mislukt'));
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateLeadFee = async () => {
    setProcessing(true);
    try {
      await api.post('/admin/suppliers/settings/lead-fee', {
        amount: newLeadFee,
        currency: 'EUR'
      });
      toast.success(`Lead fee bijgewerkt naar €${newLeadFee}`);
      setLeadFee(newLeadFee);
      setSettingsDialogOpen(false);
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Bijwerken mislukt'));
    } finally {
      setProcessing(false);
    }
  };

  const handleViewReview = async (supplier) => {
    try {
      const response = await api.get(`/admin/suppliers/reviews/pending/${supplier.id}`);
      setSelectedReview(response.data);
      setReviewDialogOpen(true);
    } catch (error) {
      toast.error('Kon wijzigingen niet laden');
    }
  };

  const handleReviewAction = async (approve) => {
    if (!selectedReview) return;
    setProcessing(true);
    
    try {
      await api.post(`/admin/suppliers/${selectedReview.supplier.id}/review`, null, {
        params: { approve, notes: reviewNotes || null }
      });
      toast.success(approve ? 'Profiel wijzigingen goedgekeurd' : 'Profiel wijzigingen afgewezen');
      setReviewDialogOpen(false);
      setReviewNotes('');
      setSelectedReview(null);
      fetchPendingReviews();
      fetchSuppliers();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Actie mislukt'));
    } finally {
      setProcessing(false);
    }
  };

  const getFieldLabel = (field) => {
    const labels = {
      company_name: 'Bedrijfsnaam',
      description: 'Beschrijving',
      services: 'Diensten',
      sectors: 'Sectoren',
      regions: "Regio's",
      website: 'Website',
      contact_email: 'E-mail',
      contact_phone: 'Telefoon',
      certifications: 'Certificeringen'
    };
    return labels[field] || field;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black dark:border-white"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">
            Admin
          </p>
          <h1 className="text-2xl font-bold tracking-tight text-black dark:text-white" data-testid="admin-marketplace-title">
            Leverancier Beheer
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            Beheer leveranciers in de marketplace
          </p>
        </div>
        <Button variant="outline" onClick={() => setSettingsDialogOpen(true)}>
          <Settings className="h-4 w-4 mr-2" />
          Lead Fee: €{leadFee}
        </Button>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="suppliers" data-testid="admin-tab-suppliers">
            Leveranciers
          </TabsTrigger>
          <TabsTrigger value="reviews" data-testid="admin-tab-reviews" className="relative">
            Profiel Reviews
            {pendingReviews.length > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-xs bg-red-500 text-white rounded-full">
                {pendingReviews.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers" className="space-y-6 mt-6">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <Clock className="h-8 w-8 text-yellow-500" />
                  <div>
                    <p className="text-2xl font-bold">{stats?.suppliers?.pending || 0}</p>
                    <p className="text-xs text-neutral-500">In behandeling</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold">{stats?.suppliers?.approved || 0}</p>
                    <p className="text-xs text-neutral-500">Goedgekeurd</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-8 w-8 text-orange-500" />
                  <div>
                    <p className="text-2xl font-bold">{stats?.suppliers?.suspended || 0}</p>
                    <p className="text-xs text-neutral-500">Geschorst</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <XCircle className="h-8 w-8 text-red-500" />
                  <div>
                    <p className="text-2xl font-bold">{stats?.suppliers?.blocked || 0}</p>
                    <p className="text-xs text-neutral-500">Geblokkeerd</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <Euro className="h-8 w-8 text-purple-500" />
                  <div>
                    <p className="text-2xl font-bold">€{stats?.revenue?.total_lead_fees?.toLocaleString() || 0}</p>
                    <p className="text-xs text-neutral-500">Lead fees</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <Input
                placeholder="Zoeken op naam, email, KvK..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter op status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle statussen</SelectItem>
                <SelectItem value="pending">In behandeling</SelectItem>
                <SelectItem value="approved">Goedgekeurd</SelectItem>
                <SelectItem value="suspended">Geschorst</SelectItem>
                <SelectItem value="blocked">Geblokkeerd</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Suppliers List */}
          {suppliers.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-neutral-500 dark:text-neutral-400">
                  <Truck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">Geen leveranciers gevonden</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {suppliers.map((supplier) => (
                <Card 
                  key={supplier.id}
                  className="cursor-pointer hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors"
                  onClick={() => handleViewDetail(supplier)}
                >
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Building2 className="h-5 w-5 text-neutral-400 flex-shrink-0" />
                          <span className="font-medium text-neutral-900 dark:text-white truncate">
                            {supplier.company_name}
                          </span>
                          <Badge className={statusColors[supplier.status] || ''}>
                            {statusLabels[supplier.status] || supplier.status}
                          </Badge>
                          {supplier.is_visible && (
                            <Badge variant="outline" className="text-green-600 border-green-200">
                              Zichtbaar
                            </Badge>
                          )}
                          {supplier.profile_review_pending && (
                            <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                              Profiel review
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-neutral-500 dark:text-neutral-400">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {supplier.contact_email}
                          </span>
                          {supplier.website && (
                            <span className="flex items-center gap-1">
                              <Globe className="h-3 w-3" />
                              {supplier.website}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {supplier.services?.slice(0, 4).map((service) => (
                            <Badge key={service} variant="secondary" className="text-xs">
                              {service}
                            </Badge>
                          ))}
                          {supplier.services?.length > 4 && (
                            <Badge variant="secondary" className="text-xs">
                              +{supplier.services.length - 4}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-neutral-400 flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Profile Reviews Tab */}
        <TabsContent value="reviews" className="space-y-6 mt-6">
          {pendingReviews.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-neutral-500 dark:text-neutral-400">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">Geen profiel wijzigingen om te beoordelen</p>
                  <p className="text-sm mt-1">Alle profiel updates zijn verwerkt.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {pendingReviews.map((supplier) => (
                <Card 
                  key={supplier.id}
                  className="cursor-pointer hover:border-blue-400 dark:hover:border-blue-600 transition-colors border-blue-200 dark:border-blue-800"
                  onClick={() => handleViewReview(supplier)}
                  data-testid={`review-card-${supplier.id}`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Building2 className="h-5 w-5 text-blue-500 flex-shrink-0" />
                          <span className="font-medium text-neutral-900 dark:text-white truncate">
                            {supplier.company_name}
                          </span>
                          <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                            Wacht op beoordeling
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-neutral-500 dark:text-neutral-400">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Ingediend: {new Date(supplier.profile_review_requested_at).toLocaleDateString('nl-NL')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {supplier.contact_email}
                          </span>
                        </div>
                        {supplier.pending_profile_changes?.changes && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {Object.keys(supplier.pending_profile_changes.changes).map((field) => (
                              <Badge key={field} variant="outline" className="text-xs">
                                {getFieldLabel(field)}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button variant="outline" size="sm">
                        Bekijken
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedSupplier && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  {selectedSupplier.supplier.company_name}
                </DialogTitle>
                <DialogDescription>
                  Geregistreerd op {new Date(selectedSupplier.supplier.created_at).toLocaleDateString('nl-NL')}
                </DialogDescription>
              </DialogHeader>

              <Tabs defaultValue="details" className="mt-4">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="users">Gebruikers</TabsTrigger>
                  <TabsTrigger value="activity">Activiteit</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4">
                  {/* Status */}
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-neutral-500">Status:</span>
                    <Badge className={statusColors[selectedSupplier.supplier.status] || ''}>
                      {statusLabels[selectedSupplier.supplier.status]}
                    </Badge>
                  </div>

                  {/* Company Info */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Bedrijfsgegevens</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-neutral-500">Land:</span>{' '}
                          {selectedSupplier.supplier.country}
                        </div>
                        <div>
                          <span className="text-neutral-500">KvK:</span>{' '}
                          {selectedSupplier.supplier.kvk_number || '-'}
                        </div>
                        <div>
                          <span className="text-neutral-500">BTW:</span>{' '}
                          {selectedSupplier.supplier.vat_number || '-'}
                        </div>
                        <div>
                          <span className="text-neutral-500">Website:</span>{' '}
                          {selectedSupplier.supplier.website ? (
                            <a href={selectedSupplier.supplier.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              {selectedSupplier.supplier.website}
                            </a>
                          ) : '-'}
                        </div>
                      </div>
                      <div>
                        <span className="text-neutral-500">Beschrijving:</span>
                        <p className="mt-1 text-neutral-900 dark:text-white">
                          {selectedSupplier.supplier.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Services & Sectors */}
                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Diensten</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-1">
                          {selectedSupplier.supplier.services?.map((s) => (
                            <Badge key={s} variant="secondary">{s}</Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Sectoren</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-1">
                          {selectedSupplier.supplier.sectors?.map((s) => (
                            <Badge key={s} variant="outline">{s}</Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Finance Summary */}
                  {selectedSupplier.ledger_summary && Object.keys(selectedSupplier.ledger_summary).length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Lead Fees</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          {Object.entries(selectedSupplier.ledger_summary).map(([status, data]) => (
                            <div key={status}>
                              <span className="text-neutral-500 capitalize">{status}:</span>{' '}
                              €{data.total} ({data.count})
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="users">
                  <div className="space-y-2">
                    {selectedSupplier.users?.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                        <div>
                          <p className="font-medium text-neutral-900 dark:text-white">{user.full_name}</p>
                          <p className="text-sm text-neutral-500">{user.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={user.role === 'admin' ? 'default' : 'outline'}>
                            {user.role}
                          </Badge>
                          {user.mfa_enabled && (
                            <Badge variant="outline" className="text-green-600">MFA</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="activity">
                  <div className="space-y-2">
                    {selectedSupplier.audit_logs?.map((log) => (
                      <div key={log.id} className="flex items-start gap-3 p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg text-sm">
                        <div className="h-2 w-2 mt-1.5 rounded-full bg-neutral-400" />
                        <div>
                          <p className="text-neutral-900 dark:text-white">{log.action.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-neutral-500">
                            {new Date(log.timestamp).toLocaleString('nl-NL')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>

              <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
                {selectedSupplier.supplier.status === 'pending' && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setApproveDialogOpen(true);
                      }}
                      className="text-red-600 border-red-200"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Afwijzen
                    </Button>
                    <Button onClick={() => setApproveDialogOpen(true)}>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Goedkeuren
                    </Button>
                  </>
                )}
                {selectedSupplier.supplier.status !== 'pending' && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => setStatusDialogOpen(true)}
                    >
                      <RefreshCcw className="h-4 w-4 mr-2" />
                      Status wijzigen
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Verwijderen
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve/Reject Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leverancier beoordelen</DialogTitle>
            <DialogDescription>
              Keur deze leverancier goed of wijs af.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Notities (optioneel)"
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => handleApprove(false)}
              disabled={processing}
              className="text-red-600"
            >
              Afwijzen
            </Button>
            <Button onClick={() => handleApprove(true)} disabled={processing}>
              Goedkeuren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Status wijzigen</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Selecteer nieuwe status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="approved">Goedgekeurd</SelectItem>
                <SelectItem value="suspended">Geschorst</SelectItem>
                <SelectItem value="blocked">Geblokkeerd</SelectItem>
                <SelectItem value="inactive">Inactief</SelectItem>
              </SelectContent>
            </Select>
            <Textarea
              placeholder="Reden (verplicht)"
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleStatusChange} disabled={!newStatus || !actionNotes || processing}>
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leverancier verwijderen</DialogTitle>
            <DialogDescription>
              Weet u zeker dat u deze leverancier wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annuleren
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={processing}>
              Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lead Fee Instellingen</DialogTitle>
            <DialogDescription>
              Stel het bedrag in dat leveranciers betalen per geaccepteerde lead.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center gap-2">
              <span className="text-lg font-medium">€</span>
              <Input
                type="number"
                value={newLeadFee}
                onChange={(e) => setNewLeadFee(parseFloat(e.target.value) || 0)}
                min={0}
                max={1000}
                step={5}
                className="w-32"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleUpdateLeadFee} disabled={processing}>
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedReview && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Profiel wijzigingen beoordelen
                </DialogTitle>
                <DialogDescription>
                  {selectedReview.supplier.company_name} - ingediend op {new Date(selectedReview.pending_changes?.submitted_at).toLocaleDateString('nl-NL')}
                </DialogDescription>
              </DialogHeader>

              <div className="py-4 space-y-4">
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  De leverancier wil de volgende wijzigingen doorvoeren:
                </p>

                {/* Changes comparison */}
                <div className="space-y-3">
                  {selectedReview.changes_comparison?.map((change) => (
                    <Card key={change.field} className="border-blue-200 dark:border-blue-800">
                      <CardContent className="py-3">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{getFieldLabel(change.field)}</Badge>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-xs text-neutral-500 mb-1">Huidige waarde:</p>
                              <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                                {Array.isArray(change.old_value) ? (
                                  <div className="flex flex-wrap gap-1">
                                    {change.old_value.map((v) => (
                                      <Badge key={v} variant="secondary" className="text-xs">{v}</Badge>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-neutral-700 dark:text-neutral-300">
                                    {change.old_value || <span className="italic text-neutral-400">Leeg</span>}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div>
                              <p className="text-xs text-neutral-500 mb-1">Nieuwe waarde:</p>
                              <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                                {Array.isArray(change.new_value) ? (
                                  <div className="flex flex-wrap gap-1">
                                    {change.new_value.map((v) => (
                                      <Badge key={v} variant="secondary" className="text-xs">{v}</Badge>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-neutral-700 dark:text-neutral-300">
                                    {change.new_value || <span className="italic text-neutral-400">Leeg</span>}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Notities (optioneel)</label>
                  <Textarea
                    placeholder="Voeg notities toe voor de leverancier..."
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setReviewDialogOpen(false);
                    setReviewNotes('');
                  }}
                >
                  Annuleren
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleReviewAction(false)}
                  disabled={processing}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="reject-profile-btn"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Afwijzen
                </Button>
                <Button 
                  onClick={() => handleReviewAction(true)} 
                  disabled={processing}
                  data-testid="approve-profile-btn"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Goedkeuren
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminSupplierManagement;
